import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../api';
import { DEFAULT_FAVORITES, VARIANT_MIGRATION_FAVORITES } from '../data/modules';
import { fetchNavigationPreferencesOnce, invalidateNavigationPreferencesCache, navigationPreferencesKey } from './navigationPreferencesCache';

const STORAGE_KEY = 'kp-favorites';
const LEGACY_VARIANT_KEY = 'kanplan-menu-variant';
const SYNC_DEBOUNCE_MS = 600;

function readStoredFavorites() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter((x) => typeof x === 'string');
    }
  } catch {}
  return null;
}

function readLegacyVariantFavorites() {
  try {
    const v = localStorage.getItem(LEGACY_VARIANT_KEY);
    if (v && VARIANT_MIGRATION_FAVORITES[v]) {
      return [...VARIANT_MIGRATION_FAVORITES[v]];
    }
  } catch {}
  return null;
}

function initialFavorites() {
  return readStoredFavorites() ?? readLegacyVariantFavorites() ?? [...DEFAULT_FAVORITES];
}

export function useFavorites() {
  const [favorites, setFavorites] = useState(() => initialFavorites());
  const hydratedRef = useRef(false);
  const syncTimerRef = useRef(null);
  // Det senast kända servervärdet, så att uppstarten inte skriver tillbaka
  // det den just hämtat (A11-uppföljning, 2026-10-07).
  const syncedKeyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchNavigationPreferencesOnce();
      if (cancelled) return;
      if (Array.isArray(data?.favorites)) {
        syncedKeyRef.current = navigationPreferencesKey(data.favorites);
        setFavorites(data.favorites);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data.favorites)); } catch {}
      }
      hydratedRef.current = true;
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!hydratedRef.current) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites)); } catch {}
    if (navigationPreferencesKey(favorites) === syncedKeyRef.current) return;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(async () => {
      try {
        const prev = (await fetchNavigationPreferencesOnce()) || {};
        await fetch(`${API_URL}/users/me/navigation-preferences`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            theme: prev.theme || 'light',
            favorites,
            recent_items: Array.isArray(prev.recent_items) ? prev.recent_items : [],
            collapsed_sections: prev.collapsed_sections || { global: [], project: [] },
          }),
        });
        syncedKeyRef.current = navigationPreferencesKey(favorites);
        invalidateNavigationPreferencesCache();
      } catch {}
    }, SYNC_DEBOUNCE_MS);
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [favorites]);

  const isFavorite = useCallback((name) => favorites.includes(name), [favorites]);

  const toggleFavorite = useCallback((name) => {
    setFavorites((cur) => {
      if (cur.includes(name)) return cur.filter((n) => n !== name);
      return [...cur, name];
    });
  }, []);

  const clearAll = useCallback(() => {
    setFavorites([]);
  }, []);

  const resetToDefaults = useCallback(() => {
    setFavorites([...DEFAULT_FAVORITES]);
  }, []);

  return { favorites, isFavorite, toggleFavorite, clearAll, resetToDefaults };
}
